import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { superAdminApi } from '../../lib/superAdminApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Plus, ArrowLeft, Trash2, Play, Package, FileText, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

const KIND_META = {
  policy: { label: 'Política', icon: FileText, color: 'text-[#5BB8E8] border-[#5BB8E8]/40' },
  evidence: { label: 'Evidência', icon: ClipboardCheck, color: 'text-emerald-300 border-emerald-500/40' },
  task: { label: 'Tarefa', icon: Package, color: 'text-[#2196C9] border-[#2196C9]/40' },
};

export default function SuperAdminCustomTemplates() {
  const { tenantId } = useParams();
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = () => {
    setLoading(true);
    superAdminApi.listCustomTemplates(tenantId).then((r) => setTemplates(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [tenantId]);

  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const applyBulk = async () => {
    if (selected.size === 0) return toast.error('Selecciona pelo menos 1 template');
    try {
      const { data } = await superAdminApi.applyCustomTemplates(tenantId, { template_ids: Array.from(selected) });
      toast.success(`Aplicados: ${data.created.policies + data.created.evidences + data.created.tasks} items criados no tenant`);
      setSelected(new Set());
    } catch (err) { toast.error(err.response?.data?.detail || 'Falha'); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminar template "${t.title}"?`)) return;
    await superAdminApi.deleteCustomTemplate(tenantId, t.template_id);
    toast.success('Eliminado');
    load();
  };

  const grouped = {
    policy: templates.filter((t) => t.kind === 'policy'),
    evidence: templates.filter((t) => t.kind === 'evidence'),
    task: templates.filter((t) => t.kind === 'task'),
  };

  return (
    <div className="space-y-6" data-testid="sa-custom-templates-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={() => nav('/super-admin/tenants')} className="text-white/70 hover:text-white hover:bg-white/5">
            <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2 text-white">
              <Package className="w-7 h-7" style={{ color: '#5BB8E8' }} /> Templates custom
            </h1>
            <p className="text-white/60 mt-1">Templates específicos para este tenant, além dos sectoriais</p>
          </div>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button
              onClick={applyBulk}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
              data-testid="sa-templates-apply-bulk"
            >
              <Play className="w-4 h-4 mr-1" /> Aplicar {selected.size} ao tenant
            </Button>
          )}
          <Button
            onClick={() => setShowCreate(true)}
            className="text-white font-semibold"
            style={{ background: '#2196C9' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#5BB8E8')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#2196C9')}
            data-testid="sa-templates-new-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Novo template
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#5BB8E8' }} /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {['policy', 'evidence', 'task'].map((kind) => {
            const meta = KIND_META[kind];
            const Icon = meta.icon;
            return (
              <Card key={kind} className="bg-[#0D1B3E]/60 border-white/10 backdrop-blur-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white flex items-center gap-2 text-base">
                    <Icon className="w-4 h-4" style={{ color: '#5BB8E8' }} /> {meta.label}s ({grouped[kind].length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {grouped[kind].length === 0 ? (
                    <div className="text-xs text-white/40 italic py-4 text-center">Nenhum {meta.label.toLowerCase()} custom</div>
                  ) : (
                    grouped[kind].map((t) => (
                      <div key={t.template_id} className="p-3 bg-[#0a0e1a]/60 border border-white/10 rounded-lg hover:border-white/20 transition-colors" data-testid={`sa-tpl-${t.template_id}`}>
                        <div className="flex items-start gap-2">
                          <Checkbox checked={selected.has(t.template_id)} onCheckedChange={() => toggle(t.template_id)} className="mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-white text-sm">{t.title}</div>
                            {t.description && <p className="text-xs text-white/50 mt-1 line-clamp-2">{t.description}</p>}
                            <div className="flex gap-2 mt-2 flex-wrap">
                              {t.category && <Badge variant="outline" className={`text-xs ${meta.color}`}>{t.category}</Badge>}
                              {t.priority && kind === 'task' && <Badge variant="outline" className="text-xs border-white/20 text-white/60">P: {t.priority}</Badge>}
                              {t.days_until_due && kind === 'task' && <Badge variant="outline" className="text-xs border-white/20 text-white/60">{t.days_until_due}d</Badge>}
                            </div>
                          </div>
                          <Button size="icon" variant="ghost" onClick={() => remove(t)} className="h-7 w-7 text-red-400 hover:bg-red-500/10">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CreateDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} tenantId={tenantId} />
    </div>
  );
}


function CreateDialog({ open, onClose, onCreated, tenantId }) {
  const [form, setForm] = useState({ title: '', kind: 'task', category: '', description: '', requirement: '', priority: 'media', days_until_due: 30 });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, days_until_due: parseInt(form.days_until_due, 10) || 30 };
      await superAdminApi.createCustomTemplate(tenantId, payload);
      toast.success('Template criado');
      setForm({ title: '', kind: 'task', category: '', description: '', requirement: '', priority: 'media', days_until_due: 30 });
      onCreated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Falha'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0D1B3E] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">Novo template custom</DialogTitle>
          <DialogDescription className="text-white/60">Cria uma política, evidência ou tarefa reutilizável.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-white/80">Título *</Label>
            <Input value={form.title} onChange={set('title')} required className="bg-[#0a0e1a] border-white/10 text-white" data-testid="sa-tpl-form-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white/80">Tipo *</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="bg-[#0a0e1a] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="policy">Política</SelectItem>
                  <SelectItem value="evidence">Evidência</SelectItem>
                  <SelectItem value="task">Tarefa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/80">Categoria</Label>
              <Input value={form.category} onChange={set('category')} placeholder="ex: auditoria" className="bg-[#0a0e1a] border-white/10 text-white" />
            </div>
          </div>
          <div>
            <Label className="text-white/80">Descrição</Label>
            <Textarea value={form.description} onChange={set('description')} className="bg-[#0a0e1a] border-white/10 text-white min-h-[80px]" />
          </div>
          {form.kind === 'evidence' && (
            <div>
              <Label className="text-white/80">Requisito legal (ex: NIS2 Art. 21)</Label>
              <Input value={form.requirement} onChange={set('requirement')} className="bg-[#0a0e1a] border-white/10 text-white" />
            </div>
          )}
          {form.kind === 'task' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80">Prioridade</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-[#0a0e1a] border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="critica">Crítica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/80">Prazo (dias)</Label>
                <Input type="number" min="1" value={form.days_until_due} onChange={set('days_until_due')} className="bg-[#0a0e1a] border-white/10 text-white" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-white/20 bg-transparent text-white/80 hover:bg-white/5 hover:text-white">Cancelar</Button>
            <Button
              type="submit"
              disabled={saving}
              className="text-white font-semibold"
              style={{ background: '#2196C9' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#5BB8E8')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#2196C9')}
              data-testid="sa-tpl-form-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
